// Incremental phone-book sync — used by Profile → "Sync my contacts" and by the
// automatic re-sync (components/ContactAutoSync). Contacts are stored per
// UserId on the server (Evt_UserContacts).
//
//   1. read the whole phone book (JSON from the phone)
//   2. ask the server for the fingerprint of every contact it already has
//   3. upload ONLY contacts that are new or whose fingerprint changed
//      (Mode 'merge': the server replaces just those contacts' rows)
// Contacts deleted from the phone stay on the server. Only ever called after
// the user agreed to the disclosure (Consent: 1 is required by the API).
import { GetUserContactsHashes, SyncUserContacts, SetUserContactsPrimary } from '../api/api';
import { readPhoneBookForSync } from './contacts';
import { contactHash } from './contactHash';

const CHUNK = 200;
const lastKey = (userId) => `se_contacts_lastsync:${userId}`;

export const lastSyncAt = (userId) => { try { return Number(localStorage.getItem(lastKey(userId)) || 0); } catch { return 0; } };
const markSynced = (userId) => { try { localStorage.setItem(lastKey(userId), String(Date.now())); } catch { /* ignore */ } };

// → { total, added, updated, unchanged } — or null when prompt=false and the
// Contacts permission is not granted.
export async function syncPhoneBook(userId, { prompt = true, onProgress } = {}) {
  const contacts = await readPhoneBookForSync({ prompt });
  if (!contacts) return null;
  // An API without the fingerprint endpoint yet → treat the server as empty
  // (everything is uploaded once; the API's upsert keeps it free of duplicates).
  const res = await GetUserContactsHashes({ UserId: userId }).catch(() => null);
  const server = (res && res.hashes) || {};
  let added = 0;
  let updated = 0;
  const changed = contacts.filter(c => {
    const have = server[c.ContactKey];
    if (!have) { added += 1; return true; }
    if (have !== contactHash(c)) { updated += 1; return true; }
    return false;
  });
  onProgress && onProgress({ done: 0, total: changed.length });
  for (let i = 0; i < changed.length; i += CHUNK) {
    const r = await SyncUserContacts({ UserId: userId, Consent: 1, Mode: 'merge', Contacts: changed.slice(i, i + CHUNK) });
    if (!r || !r.ok) throw new Error(r && r.reason === 'consent_required' ? 'Consent missing' : 'Upload refused');
    onProgress && onProgress({ done: Math.min(i + CHUNK, changed.length), total: changed.length });
  }
  // The ★ default number of every contact: only UPDATES contacts already on
  // the server (the default is not part of the fingerprint, so changing it in
  // the phone does not re-upload the contact). An API without the endpoint
  // yet is simply skipped.
  const primary = contacts
    .map(c => ({ ContactKey: c.ContactKey, Number: ((c.Numbers || []).find(n => n.IsPrimary) || {}).Number }))
    .filter(p => p.Number);
  let defaults = 0;
  for (let i = 0; i < primary.length; i += 2000) {
    const r = await SetUserContactsPrimary({ UserId: userId, Consent: 1, Primary: primary.slice(i, i + 2000) }).catch(() => null);
    defaults += (r && r.updated) || 0;
  }
  markSynced(userId);
  return { total: contacts.length, added, updated, unchanged: contacts.length - added - updated, defaults };
}
